/**
 * Assignment editor mutations — the row, the chapters and the content blocks. MATH exercise actions
 * live in `exercise-actions.ts` and are spread into the same store. Two invariants:
 *  1. Mutate in place, never rebuild — `PATCH` is a full replacement and the document may carry
 *     keys no schema describes.
 *  2. Reorder edges are no-ops (`moveWithin` is the single place that decides it).
 * No activity, interaction or rubric actions.
 */
import type { AssignmentTemplate, AssignmentTemplatePayload } from '../interfaces'
import type { MathPracticeDefinition, ReadingDefinition } from '../types'
import type { AssignmentChapter, AssignmentContentBlock, AssignmentContentBlockType, AssignmentContentOwner, AssignmentDocumentSeed, AssignmentEditorSnapshot, AssignmentEditorStore, AssignmentWorkingDocument, ExerciseContentOwner, ImageBlockPatch, TextBlockPatch } from './interfaces'

import { ID_PREFIX, mintId } from '@shared/lib/collections'
import {
  cloneJson,
  isRecord,
  moveWithin,
  neighbourId,
  patchOptional,
  patchRequired,
} from '@shared/lib/collections'

import { AssignmentType, MathExerciseType } from '../enums'
import { ASSIGNMENT_SCHEMA_VERSION } from '../../../lib/assignments/schema'
import { MATH_PRACTICE_SCHEMA_VERSION } from '../../../lib/assignments/math-practice-schema'
import { normalizeMathPracticeDocument } from '../../../lib/assignments/normalize-math-document'
import { isAssignmentMediaPath } from '../../../lib/assignments/assignment-media'
import { toSavePayload } from './getters'
import { createExercise, findExercise, pruneExerciseSelection } from './exercise-actions'

// --- pure helpers ----------------------------------------------------------

/** A block of a different type (or nothing at all) is a no-op rather than a corrupted union member. */
const patchImageBlock = (block: AssignmentContentBlock | undefined, patch: ImageBlockPatch): void => {
  if (block?.type !== 'image') {
    return
  }
  if (patch.url !== undefined) {
    // A stored block may have no `asset` or a scalar one; minting it is the only way to fix that from the UI.
    const asset: unknown = block.asset

    if (isRecord(asset)) {
      block.asset.url = patch.url
    }
    else {
      block.asset = { url: patch.url }
    }
  }
  patchRequired(block, 'alt', patch.alt)
}

/**
 * The live `screens` array (by reference), or `[]` when the stored document has nothing usable
 * there — `data` is stored unvalidated, so every read below is guarded.
 */
const chaptersOf = (state: AssignmentEditorStore): AssignmentChapter[] => {
  const definition: unknown = state.workingDocument?.definition
  const screens: unknown = isRecord(definition) ? definition.screens : null

  return Array.isArray(screens) ? screens as AssignmentChapter[] : []
}

/** Loosely typed on purpose: the row setters mirror fields both definition shapes carry. */
const definitionOf = (state: AssignmentEditorStore): Record<string, unknown> | null => {
  const definition: unknown = state.workingDocument?.definition

  return isRecord(definition) ? definition : null
}

const readingDefinitionOf = (state: AssignmentEditorStore): ReadingDefinition | null =>
  definitionOf(state) as ReadingDefinition | null

const findChapter = (state: AssignmentEditorStore, chapterId: string): AssignmentChapter | null =>
  chaptersOf(state).find(chapter => chapter?.id === chapterId) ?? null

const isExerciseOwner = (owner: AssignmentContentOwner): owner is ExerciseContentOwner =>
  'exerciseId' in owner && typeof owner.exerciseId === 'string'

/**
 * A chapter's `content` or an exercise's `prompt` — the same block list under two owners, so one set
 * of block actions serves both editors. A chapter owner's `activityId` addresses nothing here.
 */
const findContent = (
  state: AssignmentEditorStore,
  owner: AssignmentContentOwner,
): AssignmentContentBlock[] | null => {
  if (isExerciseOwner(owner)) {
    const prompt: unknown = findExercise(state, owner.exerciseId)?.prompt

    return Array.isArray(prompt) ? prompt as AssignmentContentBlock[] : null
  }
  if (owner.activityId) {
    return null
  }
  const content: unknown = findChapter(state, owner.screenId)?.content

  return Array.isArray(content) ? content as AssignmentContentBlock[] : null
}

interface EnsuredChapterId {
  id: string
  /** The id was minted just now, which is a document edit. */
  minted: boolean
}

/**
 * The id of the chapter at `index`, minting one when the stored document left it unnamed — the
 * rail has no other way to hand such a chapter to the id-addressed editor, and the next save then
 * persists the repair. `null` when nothing object-shaped sits there.
 */
const ensureChapterId = (state: AssignmentEditorStore, index: number): EnsuredChapterId | null => {
  const chapter: unknown = chaptersOf(state)[index]

  if (!isRecord(chapter)) {
    return null
  }
  if (typeof chapter.id === 'string' && chapter.id) {
    return { id: chapter.id, minted: false }
  }
  const minted = mintId(ID_PREFIX.screen)
  chapter.id = minted

  return { id: minted, minted: true }
}

/** Positional removal: works for a chapter with no readable id, or none that is an object at all. */
const removeChapterAt = (state: AssignmentEditorStore, index: number): void => {
  const chapters = chaptersOf(state)

  if (index < 0 || index >= chapters.length) {
    return
  }
  const [removed] = chapters.splice(index, 1)
  const removedId: unknown = isRecord(removed) ? removed.id : null

  if (typeof removedId === 'string' && state.selectedChapterId === removedId) {
    state.selectedChapterId = neighbourId(chapters, index)
  }
}

const pruneSelection = (state: AssignmentEditorStore): void => {
  const chapters = chaptersOf(state)
  if (state.selectedChapterId && chapters.some(chapter => chapter?.id === state.selectedChapterId)) {
    return
  }
  state.selectedChapterId = chapters[0]?.id ?? null
}

// --- media manifest --------------------------------------------------------
// Appended on upload, reconciled on save. The cover is a row column, never part of the manifest.

const collectMediaPath = (value: unknown, into: Set<string>): void => {
  if (typeof value !== 'string') {
    return
  }
  const trimmed = value.trim()
  if (isAssignmentMediaPath(trimmed)) {
    into.add(trimmed)
  }
}

/**
 * Reads by shape, not `block.type` — the discriminant is authored data too. Covers `asset.url`
 * (image/audio) and `TextBlock.audioUrl`, which a stored document may carry despite no audio UI.
 */
const collectContentMediaPaths = (content: unknown, into: Set<string>): void => {
  if (!Array.isArray(content)) {
    return
  }
  content.forEach((block: unknown) => {
    if (!isRecord(block)) {
      return
    }
    if (isRecord(block.asset)) {
      collectMediaPath(block.asset.url, into)
    }
    collectMediaPath(block.audioUrl, into)
  })
}

/** A MATH set's images live in its exercise prompts. */
const collectExerciseMediaPaths = (exercises: unknown, into: Set<string>): void => {
  if (!Array.isArray(exercises)) {
    return
  }
  exercises.forEach((exercise: unknown) => {
    if (isRecord(exercise)) {
      collectContentMediaPaths(exercise.prompt, into)
    }
  })
}

/**
 * Walks both shapes, whatever the template type: chapters (and their activities — a stored document
 * may carry activities this editor never authors) and exercise prompts.
 */
const collectDocumentMediaPaths = (document: AssignmentWorkingDocument | null): string[] => {
  const found = new Set<string>()
  const definition: unknown = document?.definition
  const screens: unknown = isRecord(definition) ? definition.screens : null

  if (isRecord(definition)) {
    collectExerciseMediaPaths(definition.exercises, found)
  }

  if (!Array.isArray(screens)) {
    return [...found]
  }

  screens.forEach((screen: unknown) => {
    if (!isRecord(screen)) {
      return
    }
    collectContentMediaPaths(screen.content, found)

    if (!Array.isArray(screen.activities)) {
      return
    }
    screen.activities.forEach((activity: unknown) => {
      if (!isRecord(activity)) {
        return
      }
      collectContentMediaPaths(activity.content, found)
    })
  })

  return [...found]
}

/** A document with no media keeps no `media` key: writing `[]` would make an untouched document dirty. */
const reconcileMediaManifest = (state: AssignmentEditorStore): void => {
  if (!state.workingDocument) {
    return
  }
  const paths = collectDocumentMediaPaths(state.workingDocument)

  if (paths.length === 0 && !state.workingDocument.media) {
    return
  }
  state.workingDocument.media = paths
}

// --- factories -------------------------------------------------------------

/** `ID_PREFIX` has no `assignment` member; same guarantees as `mintId` (prefixed, never numeric). */
const mintDefinitionId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.()
  const suffix = uuid || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

  return `assignment-${suffix}`
}

/**
 * No `cta` / `isLast` — absent is the default. Born with one empty text block so a fresh chapter
 * already satisfies the shared `Screen` refinement.
 */
const createChapter = (): AssignmentChapter => ({
  id: mintId(ID_PREFIX.screen),
  layout: 'single',
  content: [{ type: 'text', value: '' }],
  activities: [],
})

/**
 * The document a new template starts with, WITHOUT installing it — `createEmptyDocument` installs
 * it. READING seeds one chapter, MATH one multiple choice exercise; both already satisfy their
 * schema's structural minimums.
 */
export const buildEmptyDocument = (seed: AssignmentDocumentSeed): AssignmentWorkingDocument => {
  const base = {
    id: mintDefinitionId(),
    title: seed.title,
    grade: seed.grade,
    ...(seed.description ? { description: seed.description } : {}),
  }

  if (seed.type === AssignmentType.Math) {
    const definition: MathPracticeDefinition = {
      ...base,
      schemaVersion: MATH_PRACTICE_SCHEMA_VERSION,
      disciplineCode: 'MATH',
      exercises: [createExercise(MathExerciseType.MultipleChoice)],
    }

    return { definition }
  }

  const definition: ReadingDefinition = {
    ...base,
    schemaVersion: ASSIGNMENT_SCHEMA_VERSION,
    disciplineCode: seed.disciplineCode,
    screens: [createChapter()],
  }

  return { definition }
}

const createContentBlock = (type: AssignmentContentBlockType): AssignmentContentBlock => {
  if (type === 'image') {
    return { type: 'image', asset: { url: '' }, alt: '' }
  }
  if (type === 'audio') {
    return { type: 'audio', asset: { url: '' } }
  }
  return { type: 'text', value: '' }
}

const takeSnapshot = (state: AssignmentEditorStore): AssignmentEditorSnapshot | null => {
  if (!state.workingDocument) {
    return null
  }
  return {
    title: state.title,
    type: state.type,
    status: state.status,
    disciplineCode: state.disciplineCode,
    grade: state.grade,
    coverPath: state.coverPath,
    coverUrl: state.coverUrl,
    curriculumTopicId: state.curriculumTopicId,
    curriculumLessonId: state.curriculumLessonId,
    taxonomyLabels: state.taxonomyLabels ? { ...state.taxonomyLabels } : null,
    document: cloneJson(state.workingDocument),
  }
}

/**
 * The one place a template becomes editor state. The document is cloned whole and stored
 * unparsed — never Zod-strip on the way in, this state is what gets saved back. The one repair is
 * MATH's `null` → `''` (see `normalizeMathPracticeDocument`), which only undoes the backend's
 * empty-string conversion.
 * `?? null` stays although the API guarantees the keys: older cached payloads reach here too.
 */
const applyTemplate = (
  state: AssignmentEditorStore,
  template: AssignmentTemplate,
  keepSelection: boolean,
): void => {
  state.templateId = template.id
  state.title = template.title
  state.type = template.type
  state.status = template.status
  state.disciplineCode = template.discipline_code
  state.grade = template.grade
  state.coverPath = template.cover_path ?? null
  state.coverUrl = template.cover_url ?? null
  state.curriculumTopicId = template.curriculum_topic_id ?? null
  state.curriculumLessonId = template.curriculum_lesson_id ?? null
  state.taxonomyLabels = {
    curriculum_topic_id: template.curriculum_topic_id ?? null,
    curriculum_lesson_id: template.curriculum_lesson_id ?? null,
    module_number: template.module_number ?? null,
    module_name: template.module_name ?? null,
    topic_name: template.topic_name ?? null,
    lesson_number: template.lesson_number ?? null,
    lesson_name: template.lesson_name ?? null,
    is_stale: template.is_stale === true,
  }
  const document = template.type === AssignmentType.Math
    ? normalizeMathPracticeDocument(template.data)
    : template.data
  state.workingDocument = cloneJson(document) as AssignmentWorkingDocument
  state.pristine = takeSnapshot(state)
  state.mediaUrls = { ...(template.media ?? {}) }
  state.mediaExpiresAt = template.media_expires_at ?? null
  state.validationIssues = null
  if (!keepSelection) {
    state.selectedChapterId = null
    state.selectedExerciseId = null
  }
  pruneSelection(state)
  pruneExerciseSelection(state)
}

// --- Actions ---------------------------------------------------------------

export const actions = {
  // ---- lifecycle -----------------------------------------------------------

  loadTemplate (this: AssignmentEditorStore, payload: AssignmentTemplate): void {
    applyTemplate(this, payload, false)
  },

  /** Same as `loadTemplate` but the selection survives; re-snapshotting is what clears `isDirty`. */
  applySavedTemplate (this: AssignmentEditorStore, payload: AssignmentTemplate): void {
    applyTemplate(this, payload, true)
  },

  /**
   * Back to pristine. Restores `coverUrl` / `taxonomyLabels` too: `setCoverPath(null)` and
   * `setCurriculumTopicId` clear them, so a reset that skipped them would leave a discarded edit on
   * screen. No Discard control calls this yet.
   */
  reset (this: AssignmentEditorStore): void {
    if (!this.pristine) {
      return
    }
    this.title = this.pristine.title
    this.type = this.pristine.type
    this.status = this.pristine.status
    this.disciplineCode = this.pristine.disciplineCode
    this.grade = this.pristine.grade
    this.coverPath = this.pristine.coverPath
    this.coverUrl = this.pristine.coverUrl
    this.curriculumTopicId = this.pristine.curriculumTopicId
    this.curriculumLessonId = this.pristine.curriculumLessonId
    this.taxonomyLabels = this.pristine.taxonomyLabels
      ? { ...this.pristine.taxonomyLabels }
      : null
    this.workingDocument = cloneJson(this.pristine.document)
    this.validationIssues = null
    pruneSelection(this)
    pruneExerciseSelection(this)
  },

  clear (this: AssignmentEditorStore): void {
    this.templateId = null
    this.title = ''
    this.type = null
    this.status = null
    this.disciplineCode = null
    this.grade = null
    this.coverPath = null
    this.coverUrl = null
    this.curriculumTopicId = null
    this.curriculumLessonId = null
    this.taxonomyLabels = null
    this.workingDocument = null
    this.pristine = null
    this.selectedChapterId = null
    this.selectedExerciseId = null
    this.promptTab = 'editor'
    this.mediaUrls = {}
    this.mediaExpiresAt = null
    this.validationIssues = null
    this.loading = false
    this.saving = false
  },

  setLoading (this: AssignmentEditorStore, payload: AssignmentEditorStore['loading']): void {
    this.loading = payload
  },

  setSaving (this: AssignmentEditorStore, payload: AssignmentEditorStore['saving']): void {
    this.saving = payload
  },

  // ---- document lifecycle --------------------------------------------------

  /** Installs a document by reference (not cloned). `pristine` is left alone — this is an edit. */
  setDocument (this: AssignmentEditorStore, payload: AssignmentWorkingDocument): void {
    this.workingDocument = payload
    this.validationIssues = null
    this.selectedChapterId = null
    this.selectedExerciseId = null
    pruneSelection(this)
    pruneExerciseSelection(this)
  },

  /** The document half of `reset`: row fields are left alone. */
  resetDocument (this: AssignmentEditorStore): void {
    if (!this.pristine) {
      return
    }
    this.workingDocument = cloneJson(this.pristine.document)
    this.validationIssues = null
    pruneSelection(this)
    pruneExerciseSelection(this)
  },

  /**
   * Mints, installs and returns the document a new assignment starts with, so the create hook can
   * put the same object in the `POST` body. The seed's `type` picks the shape. `pristine` stays
   * `null` until the `store` response loads.
   */
  createEmptyDocument (this: AssignmentEditorStore, seed: AssignmentDocumentSeed): AssignmentWorkingDocument {
    const document = buildEmptyDocument(seed)

    this.workingDocument = document
    this.validationIssues = null
    this.selectedChapterId = null
    this.selectedExerciseId = null
    pruneSelection(this)
    pruneExerciseSelection(this)

    return document
  },

  // ---- row-level fields ----------------------------------------------------

  /** The catalogue handle, not the kid-facing book name (`setDefinitionTitle`). */
  setTitle (this: AssignmentEditorStore, payload: AssignmentEditorStore['title']): void {
    this.title = payload
  },

  setType (this: AssignmentEditorStore, payload: AssignmentEditorStore['type']): void {
    this.type = payload
  },

  /** Not gated here — the caller checks `isPublishable` before offering `PUBLISHED`. */
  setStatus (this: AssignmentEditorStore, payload: AssignmentEditorStore['status']): void {
    this.status = payload
  },

  /** Mirrored into the definition when set; clearing is not, since the definition requires the field. */
  setDisciplineCode (this: AssignmentEditorStore, payload: AssignmentEditorStore['disciplineCode']): void {
    this.disciplineCode = payload
    const definition = definitionOf(this)

    if (definition && payload) {
      definition.disciplineCode = payload
    }
  },

  setGrade (this: AssignmentEditorStore, payload: AssignmentEditorStore['grade']): void {
    this.grade = payload
    const definition = definitionOf(this)

    if (definition && payload) {
      definition.grade = payload
    }
  },

  /** The cover's object path — a row column, never part of `data.media`. `null` also drops the signed URL. */
  setCoverPath (this: AssignmentEditorStore, payload: AssignmentEditorStore['coverPath']): void {
    this.coverPath = payload
    if (payload === null) {
      this.coverUrl = null
    }
  },

  /** Response-only signed URL, never written back. */
  setCoverUrl (this: AssignmentEditorStore, payload: AssignmentEditorStore['coverUrl']): void {
    this.coverUrl = payload
  },

  /**
   * Changing the topic clears the lesson (a lesson belongs to exactly one topic; the API rejects a
   * mismatched pair) and the denormalized labels, which describe the saved tagging.
   */
  setCurriculumTopicId (this: AssignmentEditorStore, payload: AssignmentEditorStore['curriculumTopicId']): void {
    if (this.curriculumTopicId === payload) {
      return
    }
    this.curriculumTopicId = payload
    this.curriculumLessonId = null
    this.taxonomyLabels = null
  },

  /** A lesson rides on its topic: without one there is nothing for it to belong to, and the API
   *  rejects the pair, so a lesson id is refused (not just cleared) while the topic is `null`. */
  setCurriculumLessonId (this: AssignmentEditorStore, payload: AssignmentEditorStore['curriculumLessonId']): void {
    if (payload !== null && this.curriculumTopicId === null) {
      return
    }
    if (this.curriculumLessonId === payload) {
      return
    }
    this.curriculumLessonId = payload
    this.taxonomyLabels = null
  },

  // ---- definition-level fields ---------------------------------------------

  setDefinitionTitle (this: AssignmentEditorStore, payload: string): void {
    const definition = definitionOf(this)

    if (definition) {
      definition.title = payload
    }
  },

  /** Emptied ⇒ the key is deleted rather than stored as `''`. */
  setDefinitionDescription (this: AssignmentEditorStore, payload: string): void {
    const definition = definitionOf(this)

    if (definition) {
      patchOptional(definition, 'description', payload.trim() || null)
    }
  },

  // ---- selection -----------------------------------------------------------

  selectChapter (this: AssignmentEditorStore, chapterId: string | null): void {
    this.selectedChapterId = chapterId
  },

  // ---- chapters ------------------------------------------------------------

  /** Appends, selects and returns the new chapter id; `null` when no document is loaded. */
  addChapter (this: AssignmentEditorStore): string | null {
    const definition = readingDefinitionOf(this)

    if (!definition) {
      return null
    }
    const screens: unknown = definition.screens

    if (!Array.isArray(screens)) {
      // An absent `screens` is minted; a present-but-wrong-typed one is authored data we don't overwrite.
      if (screens !== undefined && screens !== null) {
        return null
      }
      definition.screens = []
    }
    const chapter = createChapter()
    definition.screens.push(chapter)
    this.selectedChapterId = chapter.id

    return chapter.id
  },

  removeChapter (this: AssignmentEditorStore, chapterId: string): void {
    removeChapterAt(this, chaptersOf(this).findIndex(chapter => chapter?.id === chapterId))
  },

  moveChapterUp (this: AssignmentEditorStore, chapterId: string): void {
    const chapters = chaptersOf(this)
    moveWithin(chapters, chapters.findIndex(chapter => chapter?.id === chapterId), -1)
  },

  moveChapterDown (this: AssignmentEditorStore, chapterId: string): void {
    const chapters = chaptersOf(this)
    moveWithin(chapters, chapters.findIndex(chapter => chapter?.id === chapterId), 1)
  },

  // Positional variants for the rail, whose chapters may carry no readable id.

  /** Selects by position. Returns whether the chapter had to be given an id first — the one case
   *  where selecting is also a document edit. */
  selectChapterAt (this: AssignmentEditorStore, index: number): boolean {
    const ensured = ensureChapterId(this, index)

    if (ensured === null) {
      return false
    }
    this.selectedChapterId = ensured.id

    return ensured.minted
  },

  removeChapterAt (this: AssignmentEditorStore, index: number): void {
    removeChapterAt(this, index)
  },

  /** `delta` of `-1` moves up, `1` down; edges are no-ops. */
  moveChapterAt (this: AssignmentEditorStore, index: number, delta: -1 | 1): void {
    moveWithin(chaptersOf(this), index, delta)
  },

  // ---- content blocks ------------------------------------------------------

  addContentBlock (
    this: AssignmentEditorStore,
    owner: AssignmentContentOwner,
    type: AssignmentContentBlockType,
  ): void {
    findContent(this, owner)?.push(createContentBlock(type))
  },

  removeContentBlock (this: AssignmentEditorStore, owner: AssignmentContentOwner, index: number): void {
    const content = findContent(this, owner)
    if (!content || index < 0 || index >= content.length) {
      return
    }
    content.splice(index, 1)
  },

  moveContentBlockUp (this: AssignmentEditorStore, owner: AssignmentContentOwner, index: number): void {
    const content = findContent(this, owner)
    if (content) {
      moveWithin(content, index, -1)
    }
  },

  moveContentBlockDown (this: AssignmentEditorStore, owner: AssignmentContentOwner, index: number): void {
    const content = findContent(this, owner)
    if (content) {
      moveWithin(content, index, 1)
    }
  },

  /** A block of a different type at `index` is a no-op rather than a corrupted union member. */
  updateTextBlock (
    this: AssignmentEditorStore,
    owner: AssignmentContentOwner,
    index: number,
    patch: TextBlockPatch,
  ): void {
    const block = findContent(this, owner)?.[index]
    if (block?.type !== 'text') {
      return
    }
    patchRequired(block, 'value', patch.value)
    patchOptional(block, 'audioUrl', patch.audioUrl)
  },

  updateImageBlock (
    this: AssignmentEditorStore,
    owner: AssignmentContentOwner,
    index: number,
    patch: ImageBlockPatch,
  ): void {
    patchImageBlock(findContent(this, owner)?.[index], patch)
  },

  /**
   * The same write addressed by the block OBJECT rather than its index — for a result that lands
   * after the fact, such as an upload response. Invariant 1 (mutate in place) is what makes the
   * object a stable handle: it survives reorders and edits, while the index does not, and the
   * component that started the upload may by then be showing another chapter's block at the same
   * index. A block no longer in `owner` (removed meanwhile) is a no-op rather than a write into
   * whatever took its slot.
   */
  updateImageBlockByRef (
    this: AssignmentEditorStore,
    owner: AssignmentContentOwner,
    block: AssignmentContentBlock,
    patch: ImageBlockPatch,
  ): void {
    const content = findContent(this, owner)
    const index = content?.indexOf(block) ?? -1

    if (index < 0) {
      return
    }
    patchImageBlock(content?.[index], patch)
  },

  // ---- media manifest ------------------------------------------------------

  /**
   * Records an uploaded object path and its signed URL. Anything that is not an object path is
   * ignored — this is the guard that keeps a signed URL out of the document.
   */
  addMediaPath (this: AssignmentEditorStore, path: string, url?: string): void {
    if (!this.workingDocument) {
      return
    }
    const trimmed = typeof path === 'string' ? path.trim() : ''

    if (!isAssignmentMediaPath(trimmed)) {
      return
    }
    if (!Array.isArray(this.workingDocument.media)) {
      this.workingDocument.media = []
    }

    if (!this.workingDocument.media.includes(trimmed)) {
      this.workingDocument.media.push(trimmed)
    }
    if (url) {
      this.mediaUrls[trimmed] = url
    }
  },

  // No wholesale `setMediaUrls`: `mediaUrls` is written only by `applyTemplate` and `addMediaPath`.
  // A `?path=` re-sign is memoised in `media-asset.hook.ts`, not here.

  /**
   * The save body with a reconciled manifest — the only way to reconcile, so no save path can
   * forget it. Deliberately an action, not the `getPayload` getter, because a getter cannot mutate.
   */
  buildSavePayload (this: AssignmentEditorStore): AssignmentTemplatePayload | null {
    reconcileMediaManifest(this)

    return toSavePayload(this)
  },

  // ---- validation report ---------------------------------------------------

  /** `[]` is "checked, clean" and opens the publish gate; `null` is "not checked". */
  setValidationIssues (this: AssignmentEditorStore, payload: AssignmentEditorStore['validationIssues']): void {
    this.validationIssues = payload
  },

  clearValidationIssues (this: AssignmentEditorStore): void {
    this.validationIssues = null
  },
}
